import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Box, Button, Divider, FormControlLabel, Switch, Tab, Tabs, Typography,
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import ShopDocumentImport from '../../ShopDocumentImport';
import UnlayerEditor from '../../../../components/UnlayerEditor';
import {
    Editor as SimpleWysiwygEditor,
    EditorProvider,
    Toolbar,
    BtnBold,
    BtnItalic,
    BtnUnderline,
    BtnStrikeThrough,
    BtnNumberedList,
    BtnBulletList,
    BtnLink,
    BtnClearFormatting,
    BtnUndo,
    BtnRedo,
    Separator,
} from 'react-simple-wysiwyg';
import ConfigCard from './ConfigCard';
import { useWebshopLanguages } from '../../WebshopLanguagesContext';
import { LanguageBar } from '../../_LanguageBar';

const ALL_LANGS = [
    { code: 'nl', label: 'NL' },
    { code: 'en', label: 'EN' },
    { code: 'fr', label: 'FR' },
];
const COLUMNS = [1, 2, 3];

/**
 * "Extra footer" card. Three columns of content + a top-level toggle to
 * surface the available payment methods. Each column has an independent
 * editor mode toggle: wysiwyg (default) or unlayer (the full email-editor
 * with content blocks). Both content types are kept side-by-side per
 * column so switching modes doesn't destroy in-progress work.
 *
 * value shape:
 *   { showPaymentMethods: bool,
 *     columns: {
 *       1: {
 *         editor: 'wysiwyg' | 'unlayer',
 *         html:   { nl, en, fr },           // wysiwyg HTML
 *         design: { nl, en, fr }            // unlayer design objects
 *       },
 *       2: { … same shape … },
 *       3: { … same shape … }
 *     }
 *   }
 *
 * onChange(field, val):
 *   - 'showPaymentMethods'                 → top-level toggle
 *   - 'columnN.editor'                     → switch the column's editor mode
 *   - 'columnN.html.<lang>'                → wysiwyg HTML edit
 *   - 'columnN.design.<lang>'              → unlayer design (captured on
 *                                            edit / on copy-from / on save).
 */
// Read once at editor boot (UnlayerEditor is mount-once) — module scope so the
// 33-entry array isn't rebuilt on every render.
const UNLAYER_OPTIONS = {
    features: {
        textEditor: {
            fontSizes: Array.from({ length: 33 }, (_, i) => `${8 + i * 2}px`),
        },
    },
};

const ExtraFooterCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const [columnTab, setColumnTab] = useState(0);                    // 0..2 → Kolom 1..3
    const [langTab,   setLangTab]   = useState({ 1: 0, 2: 0, 3: 0 });
    // Only show language tabs the admin is allowed to edit (ADMIN_LANGUAGES).
    const { adminLanguages } = useWebshopLanguages();
    const LANGS = ALL_LANGS.filter(l => adminLanguages.includes(String(l.code).toUpperCase()));
    const safeLangIdx = (col) => {
        const idx = langTab[col];
        return (idx >= 0 && idx < LANGS.length) ? idx : 0;
    };

    const showPaymentMethods = value.showPaymentMethods !== false;
    const columns = value.columns || { 1: {}, 2: {}, 3: {} };

    // Per-column-language unlayer refs. We keep one set per column rather
    // than three editors per column to avoid mounting nine unlayer instances
    // (heavy). When the user switches language tab, we save the previous
    // editor's design and load the new one into the same instance.
    const unlayerRefs = {
        1: useRef(null),
        2: useRef(null),
        3: useRef(null),
    };

    const setLang = (col, idx) => setLangTab(prev => ({ ...prev, [col]: idx }));
    const activeLang = (col) => (LANGS[safeLangIdx(col)] || ALL_LANGS[0]).code;
    const editorOf = (col) => columns[col]?.editor || 'wysiwyg';

    // Refs that always reflect the current language per column without
    // re-subscribing the unlayer listener. Keeps the design:updated handler
    // closure-stable while still emitting changes against the right lang.
    const colLangRef = useRef({ 1: 'nl', 2: 'nl', 3: 'nl' });
    useEffect(() => {
        [1, 2, 3].forEach((c) => {
            colLangRef.current[c] = LANGS[langTab[c]]?.code || 'nl';
        });
    }, [langTab, LANGS]);
    // Mirror the latest setters in refs too — the unlayer onReady fires
    // exactly once per editor instance so subscribing to design:updated
    // there would otherwise capture a stale `onChange`. With refs we always
    // hit the live one.
    const onChangeRef = useRef(onChange);
    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
    // Per-column debounce timers so a rapid edit storm only kicks one sync.
    const syncTimersRef = useRef({ 1: null, 2: null, 3: null });

    const setColumnHtml = (col, lang, html) => {
        if (onChange) onChange(`column${col}.html.${lang}`, html);
    };
    const setColumnDesign = (col, lang, design) => {
        if (onChange) onChange(`column${col}.design.${lang}`, design);
    };
    const switchEditor = (col) => {
        const current = editorOf(col);
        const next    = current === 'wysiwyg' ? 'unlayer' : 'wysiwyg';
        // Going from unlayer back to the plain HTML editor will erase any
        // saved unlayer design on the next save (the server's PATCH route
        // deletes the .json sidecar files when is_via_file flips to false).
        // Confirm before throwing away that work — the actual deletion only
        // happens at save time, so the user can still revert by toggling
        // back to unlayer before clicking Save.
        if (next === 'wysiwyg') {
            const designs = columns[col]?.design || {};
            const hasAnyDesign = !!(designs.nl || designs.en || designs.fr);
            if (hasAnyDesign) {
                const ok = typeof window !== 'undefined' && typeof window.confirm === 'function'
                    ? window.confirm(tr('cfg_extra_footer_switch_warn', 'Switching back to the plain HTML editor will delete the unlayer design on save. Continue?'))
                    : true;
                if (!ok) return;
            }
        }
        if (onChange) onChange(`column${col}.editor`, next);
    };

    // Save current unlayer design to parent state when about to switch
    // language. Async — uses the editor's saveDesign callback.
    const flushUnlayer = (col, fromLang) => {
        const editor = unlayerRefs[col].current?.editor;
        if (!editor) return Promise.resolve();
        return new Promise((resolve) => {
            editor.saveDesign((design) => {
                setColumnDesign(col, fromLang, design);
                resolve();
            });
        });
    };

    const handleLangSwitch = async (col, newIdx) => {
        if (editorOf(col) === 'unlayer') {
            const fromLang = activeLang(col);
            await flushUnlayer(col, fromLang);
            // Load the new language's design into the same editor.
            const newLang = LANGS[newIdx].code;
            const editor = unlayerRefs[col].current?.editor;
            const design = columns[col]?.design?.[newLang];
            if (editor && design) {
                try { editor.loadDesign(design); } catch { /* ignore */ }
            } else if (editor) {
                // No saved design for that language yet — start blank.
                try { editor.loadBlank(); } catch { /* ignore */ }
            }
        }
        setLang(col, newIdx);
    };

    // Column tabs now MOUNT only the active column (each unlayer column is a
    // full editor.unlayer.com iframe — three at once was the single heaviest
    // thing on the Style tab). State is the source of truth: onReady reloads
    // the design and the 400ms auto-sync keeps state fresh; flushing here
    // covers edits made inside that debounce window before the unmount.
    const handleColumnSwitch = async (newIdx) => {
        const oldCol = COLUMNS[columnTab];
        if (editorOf(oldCol) === 'unlayer') {
            await flushUnlayer(oldCol, activeLang(oldCol));
        }
        setColumnTab(newIdx);
    };

    const handleCopyFrom = async (col, fromLang) => {
        const toLang = activeLang(col);
        if (fromLang === toLang) return;
        if (editorOf(col) === 'unlayer') {
            // Source design lives in parent state (the active editor only
            // ever has one language loaded).
            const design = columns[col]?.design?.[fromLang];
            if (!design) return;
            const editor = unlayerRefs[col].current?.editor;
            if (editor) {
                try { editor.loadDesign(design); } catch { /* ignore */ }
            }
            setColumnDesign(col, toLang, design);
        } else {
            const src = columns[col]?.html?.[fromLang] || '';
            setColumnHtml(col, toLang, src);
        }
    };

    return (
        <ConfigCard
            anchor="cfg-card-footer-extra"
            title={tr('cfg_extra_footer_title', 'Extra footer')}
            description={tr('cfg_extra_footer_desc', 'A second, lower band beneath the regular footer. Three columns, each with its own per-language content.')}
            theme={theme}
        >
            <Alert severity="info" sx={{ py: 0.5 }}>
                <Typography variant="body2">
                    {tr('cfg_extra_footer_payment_help', 'Indicate which payment methods are available in the extra footer.')}
                </Typography>
            </Alert>
            <FormControlLabel
                control={
                    <Switch color="success"
                        size="small"
                        checked={!!showPaymentMethods}
                        onChange={(e) => onChange && onChange('showPaymentMethods', e.target.checked)}
                    />
                }
                label={<Typography variant="body2">{tr('cfg_extra_footer_payment_toggle', 'Show payment methods in the extra footer')}</Typography>}
            />

            <Divider />

            {/* Column tabs */}
            <Tabs value={columnTab} onChange={(_e, v) => handleColumnSwitch(v)} sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none' } }}>
                {COLUMNS.map((c) => (
                    <Tab key={c} label={`${tr('cfg_extra_footer_col', 'Column')} ${c}`} />
                ))}
            </Tabs>

            {COLUMNS.map((col, colIdx) => {
                const mode = editorOf(col);
                return (
                    columnTab === colIdx && (
                    <Box key={col}>
                        {/* Per-column toolbar: language bar + editor-mode toggle on the right */}
                        <LanguageBar
                            langs={LANGS}
                            activeCode={activeLang(col)}
                            onSelect={(code) => handleLangSwitch(col, LANGS.findIndex(l => l.code === code))}
                            onCopyFrom={(code) => handleCopyFrom(col, code)}
                            tr={tr}
                            right={
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                {mode === 'unlayer' && (
                                    <ShopDocumentImport getEditor={() => unlayerRefs[col]?.current?.editor} t={t} sx={{ ml: 1 }} />
                                )}
                                <Button
                                    size="small"
                                    variant="text"
                                    startIcon={mode === 'wysiwyg' ? <CodeIcon /> : <FormatColorTextIcon />}
                                    onClick={() => switchEditor(col)}
                                    sx={{ ml: 1 }}
                                >
                                    {mode === 'wysiwyg'
                                        ? tr('cfg_extra_footer_switch_to_html', 'Use HTML editor')
                                        : tr('cfg_extra_footer_switch_to_simple', 'Use simple editor')}
                                </Button>
                                </Box>
                            }
                        />

                        {/* Editor — single instance per column. We swap content
                            on language switch (wysiwyg via state, unlayer via
                            saveDesign/loadDesign). */}
                        {mode === 'unlayer' ? (
                            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', minHeight: 360 }}>
                                <UnlayerEditor
                                    ref={unlayerRefs[col]}
                                    minHeight="360px"
                                    // `displayMode='web'` matches the legacy
                                    // admin (content-editor.component.ts:437)
                                    // — without it unlayer wraps the export
                                    // in email-mode tables and a 100vh min
                                    // height that make the storefront footer
                                    // look indented and over-tall. The
                                    // `react-email-editor` package silently
                                    // ignores this option, which is why this
                                    // card uses the direct wrapper.
                                    displayMode="web"
                                    options={UNLAYER_OPTIONS}
                                    onReady={(unlayer) => {
                                        // onReady fires once unlayer's iframe is
                                        // fully booted — the safe point to call
                                        // loadDesign. Newer versions pass the
                                        // unlayer instance as argument; older
                                        // versions only populate the ref.
                                        const lang   = activeLang(col);
                                        const design = columns[col]?.design?.[lang];
                                        const editor = (unlayer && unlayer.loadDesign) ? unlayer : unlayerRefs[col].current?.editor;
                                        if (!editor) return;
                                        if (design) {
                                            try { editor.loadDesign(design); } catch { /* ignore */ }
                                        } else {
                                            // Match legacy `loadBlank` body
                                            // settings so a brand-new column
                                            // starts at full width with no
                                            // background tint and an auto-
                                            // height wrapper.
                                            try {
                                                editor.loadBlank && editor.loadBlank({
                                                    backgroundColor: 'TRANSPARENT',
                                                    contentWidth:    '100%',
                                                    minHeight:       'auto',
                                                });
                                            } catch { /* ignore */ }
                                        }
                                        // Auto-sync edits back to parent state.
                                        // Without this the only flush points
                                        // are language-switch and copy-from, so
                                        // a user who edits and closes the modal
                                        // directly would lose their changes —
                                        // and the floating save button stays
                                        // grey because dirty never flips. The
                                        // 400ms debounce keeps us from spamming
                                        // saveDesign+exportHtml on every
                                        // keystroke inside unlayer.
                                        const sync = () => {
                                            if (syncTimersRef.current[col]) clearTimeout(syncTimersRef.current[col]);
                                            syncTimersRef.current[col] = setTimeout(() => {
                                                try {
                                                    editor.saveDesign((newDesign) => {
                                                        const currentLang = colLangRef.current[col];
                                                        if (onChangeRef.current) onChangeRef.current(`column${col}.design.${currentLang}`, newDesign);
                                                        try {
                                                            editor.exportHtml((data) => {
                                                                // Unlayer renders its outer wrapper with
                                                                // `min-height: 100vh;` which forces the
                                                                // storefront footer block to expand to a
                                                                // full viewport height. The legacy admin
                                                                // patches the same string before persisting
                                                                // — match that here so the rendered width
                                                                // and height match what the storefront has
                                                                // always shown.
                                                                const rawHtml = data?.html || '';
                                                                const fixedHtml = rawHtml.replace('min-height: 100vh;', 'min-height: auto;margin-bottom: 42px;');
                                                                if (onChangeRef.current) onChangeRef.current(`column${col}.html.${currentLang}`, fixedHtml);
                                                            });
                                                        } catch { /* ignore */ }
                                                    });
                                                } catch { /* ignore */ }
                                            }, 400);
                                        };
                                        try { editor.addEventListener('design:updated', sync); } catch { /* ignore */ }
                                    }}
                                />
                            </Box>
                        ) : (
                            <Box sx={{
                                border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden',
                                '& .rsw-editor': { minHeight: 200 },
                                '& .rsw-ce':     { minHeight: 160, padding: '10px 14px' },
                            }}>
                                <EditorProvider>
                                    <SimpleWysiwygEditor
                                        value={columns[col]?.html?.[activeLang(col)] || ''}
                                        onChange={(e) => setColumnHtml(col, activeLang(col), e.target.value)}
                                    >
                                        <Toolbar>
                                            <BtnUndo />
                                            <BtnRedo />
                                            <Separator />
                                            <BtnBold />
                                            <BtnItalic />
                                            <BtnUnderline />
                                            <BtnStrikeThrough />
                                            <Separator />
                                            <BtnNumberedList />
                                            <BtnBulletList />
                                            <Separator />
                                            <BtnLink />
                                            <BtnClearFormatting />
                                        </Toolbar>
                                    </SimpleWysiwygEditor>
                                </EditorProvider>
                            </Box>
                        )}
                    </Box>
                    )
                );
            })}
        </ConfigCard>
    );
};

ExtraFooterCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default ExtraFooterCard;
